import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { adminFetch, apiErrorMessage, errorMessage } from '../adminFetch';

interface Reglages {
  actif: boolean;
  connectUrl: string | null;
  appApiUrl: string | null;
  vannaCompanyId: string | null;
  cleRenseignee: boolean;
  taxeId: number | null;
  tauxTva: number;
  montantsStripe: 'HT' | 'TTC';
  stockageDisponible: boolean;
  updatedAt: string | null;
}

interface Evenement {
  id: string;
  eventId: string;
  type: string;
  objectId: string | null;
  spaceId: string | null;
  status: 'RECU' | 'TRAITE' | 'IGNORE' | 'ERREUR';
  error: string | null;
  tentatives: number;
  receivedAt: string;
  processedAt: string | null;
}

const LIBELLES: Record<Evenement['status'], string> = {
  RECU: 'Reçu',
  TRAITE: 'Traité',
  IGNORE: 'Ignoré',
  ERREUR: 'Erreur',
};

const date = (iso: string | null) => (iso ? new Date(iso).toLocaleString('fr-FR') : '—');

async function json<T>(res: Response, repli: string): Promise<T> {
  if (!res.ok) throw new Error(await apiErrorMessage(res, repli));
  return (await res.json()) as T;
}

/**
 * Back-office › Facturation (29/10/2026, lot 4).
 *
 * La liaison avec le dossier Vanna de SEmplyApp (par SEmply Connect), qui
 * émet la facture légale de chaque paiement Stripe ; et le journal des
 * événements Stripe, avec rejeu d'un événement en erreur.
 */
export function AdminFacturationPage() {
  return (
    <div className="adm-page">
      <h1>Facturation</h1>
      <SectionReglages />
      <SectionEvenements />
    </div>
  );
}

function SectionReglages() {
  const [r, setR] = useState<Reglages | null>(null);
  const [cle, setCle] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; texte: string } | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  useEffect(() => {
    adminFetch('/admin/facturation/reglages')
      .then((res) => json<Reglages>(res, 'Réglages illisibles.'))
      .then(setR)
      .catch((e) => setMessage({ ok: false, texte: errorMessage(e) }));
  }, []);

  if (!r) return <p role="status">{message?.texte ?? 'Chargement…'}</p>;

  const champ = <K extends keyof Reglages>(k: K, v: Reglages[K]) => setR({ ...r, [k]: v });

  const enregistrer = async (e: FormEvent) => {
    e.preventDefault();
    setOccupe('enregistrer');
    setMessage(null);
    try {
      const corps = {
        actif: r.actif,
        connectUrl: r.connectUrl || null,
        appApiUrl: r.appApiUrl || null,
        vannaCompanyId: r.vannaCompanyId || null,
        taxeId: r.taxeId || null,
        tauxTva: Number(r.tauxTva),
        montantsStripe: r.montantsStripe,
        ...(cle.trim() ? { cle: cle.trim() } : {}),
      };
      const res = await adminFetch('/admin/facturation/reglages', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(corps),
      });
      setR(await json<Reglages>(res, 'Enregistrement refusé.'));
      setCle('');
      setMessage({ ok: true, texte: 'Réglages enregistrés.' });
    } catch (err) {
      setMessage({ ok: false, texte: errorMessage(err) });
    } finally {
      setOccupe(null);
    }
  };

  const action = async (chemin: string, nom: string) => {
    setOccupe(nom);
    setMessage(null);
    try {
      const res = await adminFetch(`/admin/facturation/${chemin}`, { method: 'POST' });
      const corps = await json<Record<string, unknown>>(res, 'Action refusée.');
      const details = (corps.details as { facture: string; message: string }[] | undefined) ?? [];
      setMessage({
        ok: details.length === 0,
        texte:
          typeof corps.message === 'string'
            ? corps.message
            : `Rattrapage : ${corps.factures ?? 0} facture(s), ${corps.pdf ?? 0} PDF, ${corps.erreurs ?? 0} erreur(s).` +
              details.map((d) => `\n• ${d.facture} : ${d.message}`).join(''),
      });
    } catch (err) {
      setMessage({ ok: false, texte: errorMessage(err) });
    } finally {
      setOccupe(null);
    }
  };

  return (
    <section aria-labelledby="fac-reglages">
      <h2 id="fac-reglages">Liaison avec le dossier Vanna</h2>
      <p className="adm-hint">
        À chaque paiement Stripe, le dossier Vanna de SEmplyApp émet la facture légale (par SEmply Connect, capacités
        app.clients.write, app.factures_vente.create et app.factures_vente.read en mode DIRECT), puis le PDF est recopié
        dans l’espace du client. Désactivée, rien n’est facturé : le rattrapage reprend ensuite les trois derniers jours.
      </p>
      {!r.stockageDisponible ? (
        <p className="adm-alerte">Stockage S3 non configuré (FACTURES_S3_* ou SUPPORT_S3_*) : les PDF ne seront pas recopiés.</p>
      ) : null}
      <form className="adm-ajout" onSubmit={(e) => void enregistrer(e)}>
        <label className="adm-case">
          <input type="checkbox" checked={r.actif} onChange={(e) => champ('actif', e.target.checked)} /> Facturation active
        </label>
        <label>
          URL de l’API SEmply Connect (et non du portail Connect)
          <input value={r.connectUrl ?? ''} placeholder="https://api-connect.semply.fr" onChange={(e) => champ('connectUrl', e.target.value)} />
        </label>
        <label>
          URL de l’API App (avec /api)
          <input value={r.appApiUrl ?? ''} placeholder="https://api-app.semply.fr/api" onChange={(e) => champ('appApiUrl', e.target.value)} />
        </label>
        <label>
          UUID du dossier Vanna
          <input value={r.vannaCompanyId ?? ''} onChange={(e) => champ('vannaCompanyId', e.target.value)} />
        </label>
        <label>
          Clé d’API Connect {r.cleRenseignee ? '(renseignée — laisser vide pour la garder)' : '(à renseigner)'}
          <input type="password" autoComplete="off" value={cle} onChange={(e) => setCle(e.target.value)} />
        </label>
        <label>
          Identifiant de la taxe dans le dossier Vanna (taxeId)
          <input
            type="number"
            min={1}
            value={r.taxeId ?? ''}
            onChange={(e) => champ('taxeId', e.target.value ? Number(e.target.value) : null)}
          />
        </label>
        <label>
          Taux de TVA (%)
          <input type="number" min={0} max={100} step="0.01" value={r.tauxTva} onChange={(e) => champ('tauxTva', Number(e.target.value))} />
        </label>
        <label>
          Les prix Stripe sont
          <select value={r.montantsStripe} onChange={(e) => champ('montantsStripe', e.target.value as 'HT' | 'TTC')}>
            <option value="TTC">TTC (le client paie la TVA comprise)</option>
            <option value="HT">HT (Stripe ajoute la TVA)</option>
          </select>
        </label>
        <div>
          <button className="btn" type="submit" disabled={occupe !== null}>
            {occupe === 'enregistrer' ? 'Enregistrement…' : 'Enregistrer'}
          </button>{' '}
          <button className="btn btn-secondary" type="button" disabled={occupe !== null} onClick={() => void action('tester', 'tester')}>
            {occupe === 'tester' ? 'Test…' : 'Tester la liaison'}
          </button>{' '}
          <button className="btn btn-secondary" type="button" disabled={occupe !== null} onClick={() => void action('rattraper', 'rattraper')}>
            {occupe === 'rattraper' ? 'Rattrapage…' : 'Rattraper maintenant'}
          </button>
        </div>
      </form>
      {message ? (
        <p
          className={message.ok ? 'adm-hint' : 'adm-erreur'}
          role={message.ok ? 'status' : 'alert'}
          style={{ whiteSpace: 'pre-line' }}
        >
          {message.texte}
        </p>
      ) : null}
    </section>
  );
}

function SectionEvenements() {
  const [filtres, setFiltres] = useState({ type: '', status: '', spaceId: '' });
  const [page, setPage] = useState(1);
  const [donnees, setDonnees] = useState<{ lignes: Evenement[]; total: number; taille: number } | null>(null);
  const [detail, setDetail] = useState<(Evenement & { payload: unknown }) | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    const q = new URLSearchParams({ page: String(page) });
    for (const [k, v] of Object.entries(filtres)) if (v) q.set(k, v);
    try {
      setDonnees(await json(await adminFetch(`/admin/facturation/evenements?${q}`), 'Journal illisible.'));
    } catch (e) {
      setErreur(errorMessage(e));
    }
  }, [filtres, page]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const ouvrir = async (id: string) => {
    try {
      setDetail(await json(await adminFetch(`/admin/facturation/evenements/${id}`), 'Événement illisible.'));
    } catch (e) {
      setErreur(errorMessage(e));
    }
  };

  const rejouer = async (id: string) => {
    try {
      setDetail(await json(await adminFetch(`/admin/facturation/evenements/${id}/rejouer`, { method: 'POST' }), 'Rejeu refusé.'));
      await charger();
    } catch (e) {
      setErreur(errorMessage(e));
    }
  };

  return (
    <section aria-labelledby="fac-evenements">
      <h2 id="fac-evenements">Événements Stripe</h2>
      <div className="adm-ajout">
        <label>
          Type
          <input value={filtres.type} placeholder="invoice., charge.refunded…" onChange={(e) => setFiltres({ ...filtres, type: e.target.value })} />
        </label>
        <label>
          Statut
          <select value={filtres.status} onChange={(e) => setFiltres({ ...filtres, status: e.target.value })}>
            <option value="">Tous</option>
            {Object.entries(LIBELLES).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label>
          Espace (UUID)
          <input value={filtres.spaceId} onChange={(e) => setFiltres({ ...filtres, spaceId: e.target.value.trim() })} />
        </label>
      </div>
      {erreur ? (
        <p className="adm-erreur" role="alert">
          {erreur}
        </p>
      ) : null}
      {donnees ? (
        <>
          <table className="adm-table">
            <thead>
              <tr>
                <th>Reçu le</th>
                <th>Type</th>
                <th>Objet</th>
                <th>Statut</th>
                <th>Tentatives</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {donnees.lignes.map((e) => (
                <tr key={e.id}>
                  <td>{date(e.receivedAt)}</td>
                  <td>{e.type}</td>
                  <td>{e.objectId ?? '—'}</td>
                  <td title={e.error ?? undefined}>{LIBELLES[e.status]}</td>
                  <td>{e.tentatives}</td>
                  <td>
                    <button type="button" className="adm-link" onClick={() => void ouvrir(e.id)}>
                      Détail
                    </button>
                    {e.status === 'ERREUR' || e.status === 'RECU' ? (
                      <>
                        {' · '}
                        <button type="button" className="adm-link" onClick={() => void rejouer(e.id)}>
                          Rejouer
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="adm-hint">
            {donnees.total} événement(s).{' '}
            {page > 1 ? (
              <button type="button" className="adm-link" onClick={() => setPage(page - 1)}>
                Précédents
              </button>
            ) : null}{' '}
            {page * donnees.taille < donnees.total ? (
              <button type="button" className="adm-link" onClick={() => setPage(page + 1)}>
                Suivants
              </button>
            ) : null}
          </p>
        </>
      ) : (
        <p role="status">Chargement…</p>
      )}
      {detail ? (
        <div className="adm-ajout" role="dialog" aria-label="Détail de l’événement">
          <p>
            <strong>{detail.type}</strong> — {detail.eventId} — {LIBELLES[detail.status]}
            {detail.error ? ` : ${detail.error}` : ''}
          </p>
          <pre style={{ maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{JSON.stringify(detail.payload, null, 2)}</pre>
          <button type="button" className="btn btn-secondary" onClick={() => setDetail(null)}>
            Fermer
          </button>
        </div>
      ) : null}
    </section>
  );
}
